import React, { ReactNode } from 'react';
import { connect } from 'react-redux';

import displayValues from '../../resources/displayValues.json';
import { formatReadableDateShort, safeParseISO } from 'shared/util/datetime-fns';
import { generateDateText } from './travelTime/TravelMonths';
import { destinationDisplayName } from './destination/DestinationList';

import { Filters, PaleState } from '../../interfaces';

export type ResultFilterPreviewProps = Pick<
    Filters,
    'origin' | 'destinations' | 'departureDate' | 'returnDate' | 'travelMonths' | 'duration' | 'ignoredFilters'
>;

export const ResultFilterPreview: React.FC<ResultFilterPreviewProps> = ({
    origin,
    destinations,
    departureDate,
    returnDate,
    travelMonths,
    duration,
    ignoredFilters,
}) => {
    const originDisplay = displayValues.originAirports.find((displayOrigin) => displayOrigin.value == origin);
    const parsedDepartureDate = safeParseISO(departureDate?.date);
    const parsedReturnDate = safeParseISO(returnDate?.date);

    const dateInfo = (): ReactNode => {
        if (parsedDepartureDate && parsedReturnDate && !ignoredFilters.includes('departureDate')) {
            return (
                <div data-testid="resultFilterPreview-dateInfo-fixed">
                    <span className="u-no-break">{formatReadableDateShort(parsedDepartureDate)}</span>
                    <span className="icon-pre icon--one-way-arrow u-mr8 u-ml8" />
                    <span className="u-no-break">{formatReadableDateShort(parsedReturnDate)}</span>
                </div>
            );
        }
        if (travelMonths.length !== 0 || (duration && !ignoredFilters.includes('travelMonths'))) {
            return <div data-testid="resultFilterPreview-dateInfo-flex">{generateDateText(travelMonths, duration)}</div>;
        }
        return <div data-testid="resultFilterPreview-dateInfo-default">Når som helst</div>;
    };

    const extraDestinations = (): string => (destinations.list.length < 2 ? '' : ` + ${destinations.list.length - 1}`);

    const locationInfo = (): ReactNode => {
        const originText = originDisplay ? originDisplay.text : 'All flyplasser';
        const destinationText =
            destinations.list.length === 0 ? 'Hvor som helst' : `${destinationDisplayName(destinations.list[0])}${extraDestinations()}`;

        return (
            <div className="u-caption" data-testid="resultFilterPreview-origin-destination">
                {originText} - {destinationText}
            </div>
        );
    };

    return (
        <div data-testid="resultFilterPreview">
            {dateInfo()}
            {locationInfo()}
        </div>
    );
};

const mapStateToProps = (state: PaleState): ResultFilterPreviewProps => ({
    origin: state.filters.origin,
    destinations: state.filters.destinations,
    departureDate: state.filters.departureDate,
    returnDate: state.filters.returnDate,
    ignoredFilters: state.filters.ignoredFilters,
    travelMonths: state.filters.travelMonths,
    duration: state.filters.duration,
});

export default connect(mapStateToProps)(ResultFilterPreview);
